import { costFormater } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { AccumulatedItem } from '@/modules/deductibles/SpendTracker/hooks/useDeductibles.ts';

import { DeductiblePreviewResponse } from '../queries/useDeductiblePreview.query';

export const EFFECTIVE_DATE_ERROR = 'Effective date';
export const EFFECTIVE_DATE_RANGE_ERROR = 'is not in the valid range';

export enum DeductibleErrorType {
  Info = 'info',
  Server = 'server',
}

export const MAX_AMOUNT_ERROR_MESSAGE =
  'FamilyDeductibleAmount cannot be greater than maximum family trigger amount:';
export const MAX_AMOUNT_RE = /maximum family trigger amount:\s*([0-9][\d,]*(?:\.\d{2})?)/i;

export const MAX_INDIVIDUAL_AMOUNT_ERROR_MESSAGE =
  'IndividualDeductibleAmount cannot be greater than maximum embedded trigger amount:';
export const MAX_INDIVIDUAL_AMOUNT_RE = /maximum embedded trigger amount:\s*([0-9][\d,]*(?:\.\d{2})?)/i;

export const formatEffectiveDateError = (message: string): string => {
  const dateMatch = message.match(/Effective date (\d{4}-\d{2}-\d{2})/);
  const rangeMatch = message.match(/\{(\d{4}-\d{2}-\d{2});(\d{4}-\d{2}-\d{2})\}/);

  if (!dateMatch || !rangeMatch) return message;

  const date = dateMatch[1];
  const rangeStart = rangeMatch[1];
  const rangeEnd = rangeMatch[2];

  return `Effective date ${dayjs(date).format(DEFAULT_DATE_FORMAT)} is not in the valid range ${dayjs(rangeStart).format(DEFAULT_DATE_FORMAT)} - ${dayjs(rangeEnd).format(DEFAULT_DATE_FORMAT)}`;
};

export const parseMaxFromMessage = (msg: string): number | null => {
  const match = msg.match(MAX_AMOUNT_RE);
  if (!match) return null;
  const num = parseFloat(match[1].replace(/,/g, ''));
  return Number.isFinite(num) ? num : null;
};

export const parseMaxIndividualFromMessage = (msg: string): number | null => {
  const match = msg.match(MAX_INDIVIDUAL_AMOUNT_RE);
  if (!match) return null;
  const num = parseFloat(match[1].replace(/,/g, ''));
  return Number.isFinite(num) ? num : null;
};

export const getMaxDeductibleError = (message: string, costFormatter: typeof costFormater) => {
  const max = parseMaxFromMessage(message);
  return max != null ? `Max family deductible is ${costFormatter(max, true)} for your health plan.` : message;
};

export const getTierInfoMessage = (tier?: string) =>
  tier ? `Tier "${tier}" will be applied to this family with the entered amount.` : '';

export const getIndividualTierInfoMessage = (tier?: string) =>
  tier ? `Tier "${tier}" will be applied to this family member.` : '';

export const getMemberKey = (item: AccumulatedItem) => item.dependentId ?? item.employeeId;

export const isEmptyValue = (value: unknown) => value === '' || value == null;

export const isZeroEmptyTransition = (prev: string | null, next: string) =>
  (prev === '' && next === '0') || (prev === '0' && next === '');

export const mapIndividualPreview = (
  individualDeductibles: DeductiblePreviewResponse['individual_deductibles']
) => {
  const previewMap: Record<string, { tier?: string; is_tier_changed?: boolean }> = {};
  individualDeductibles.forEach((individual) => {
    const id = individual.dependent_id != null ? individual.dependent_id : individual.employee_id;
    previewMap[id] = { tier: individual.tier, is_tier_changed: individual.is_tier_changed };
  });
  return previewMap;
};
